import type { Level, SessionSummary } from "@backstory/protocol";
import { request } from "../api";

/** Session search facets. Every field is sent to the paginated sessions API. */
export interface SessionFilters {
  q?: string;
  hasErrors?: boolean;
  hasGaps?: boolean;
  browser?: string;
  os?: string;
  device?: SessionSummary["device"];
  level?: Level;
  minDurationSec?: number;
  maxDurationSec?: number;
  from?: string; // ISO date (yyyy-mm-dd)
  to?: string;
  user?: string;
  urlContains?: string;
  release?: string;
  reviewed?: "reviewed" | "unreviewed";
  viewed?: "viewed" | "unviewed";
  /** Feature name recorded on the session, and the variant it had. */
  flag?: string;
  flagValue?: string;
  /** ISO country code. */
  country?: string;
}

export const FILTER_KEYS: Array<keyof SessionFilters> = ["q", "hasErrors", "hasGaps", "browser", "os", "device", "level", "minDurationSec", "maxDurationSec", "from", "to", "user", "urlContains", "release", "reviewed", "viewed", "flag", "flagValue", "country"];

export function filtersFromSearch(search: Record<string, unknown>): SessionFilters {
  const f: SessionFilters = {};
  for (const k of FILTER_KEYS) {
    const v = search[k];
    if (v === undefined || v === "" || v === null) continue;
    if (k === "hasErrors" || k === "hasGaps") (f as Record<string, unknown>)[k] = v === true || v === "true" || v === "1";
    else if (k === "minDurationSec" || k === "maxDurationSec") (f as Record<string, unknown>)[k] = Number(v);
    else (f as Record<string, unknown>)[k] = String(v);
  }
  return f;
}

export function filtersToSearch(f: SessionFilters): Record<string, string | boolean | number> {
  const out: Record<string, string | boolean | number> = {};
  for (const k of FILTER_KEYS) {
    const v = f[k];
    if (v === undefined || v === "" || v === false) continue;
    out[k] = v as string | boolean | number;
  }
  return out;
}

/** Remove empty URL state before it becomes an API query/cache key. */
export function serverParams(f: SessionFilters): SessionFilters {
  return Object.fromEntries(
    Object.entries(f).filter(([, value]) => value !== undefined && value !== "" && value !== false),
  ) as SessionFilters;
}

/** Pure filter used by tests and offline consumers; the page itself filters on the server. */
export function applyClientFilters(items: SessionSummary[], f: SessionFilters, ua: (s: SessionSummary) => { browser: string; os: string; device: string }): SessionSummary[] {
  return items.filter((s) => {
    const u = ua(s);
    if (f.hasGaps && !s.hasGaps) return false;
    if (f.browser && (s.browser ?? u.browser) !== f.browser) return false;
    if (f.os && (s.os ?? u.os) !== f.os) return false;
    if (f.device && (s.device ?? u.device) !== f.device) return false;
    if (f.level && s.level !== f.level) return false;
    if (f.minDurationSec != null && s.durationMs < f.minDurationSec * 1000) return false;
    if (f.maxDurationSec != null && s.durationMs > f.maxDurationSec * 1000) return false;
    if (f.from && new Date(s.startedAt) < new Date(f.from)) return false;
    if (f.to && new Date(s.startedAt) > new Date(`${f.to}T23:59:59.999Z`)) return false;
    if (f.user && !`${s.endUserName ?? ""}\n${s.endUserId ?? ""}`.toLowerCase().includes(f.user.toLowerCase())) return false;
    if (f.urlContains && !s.url.toLowerCase().includes(f.urlContains.toLowerCase())) return false;
    if (f.release && s.release !== f.release) return false;
    if (f.country && s.countryCode !== f.country) return false;
    if (f.reviewed === "reviewed" && !s.reviewedAt) return false;
    if (f.reviewed === "unreviewed" && s.reviewedAt) return false;
    if (f.viewed === "viewed" && !s.lastViewedAt) return false;
    if (f.viewed === "unviewed" && s.lastViewedAt) return false;
    return true;
  });
}

export function activeFilterCount(f: SessionFilters): number {
  return FILTER_KEYS.filter((k) => k !== "q" && f[k] !== undefined && f[k] !== "" && f[k] !== false).length;
}

// ---------------------------------------------------------------------------
// Saved segments. The project API is the source of truth. localStorage is only
// the copy this browser made before that, and is uploaded once it can be.
// ---------------------------------------------------------------------------

export interface SavedSegment {
  id: string;
  name: string;
  filters: SessionFilters;
  createdAt: string;
}

const LS_KEY = "backstory-saved-segments";

export function loadSegments(projectId?: string): SavedSegment[] {
  try {
    const all = JSON.parse(localStorage.getItem(LS_KEY) ?? "{}") as Record<string, SavedSegment[]>;
    return all[projectId ?? "_"] ?? [];
  } catch {
    return [];
  }
}

export function saveSegment(projectId: string | undefined, seg: SavedSegment): SavedSegment[] {
  const all = (() => {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY) ?? "{}") as Record<string, SavedSegment[]>;
    } catch {
      return {};
    }
  })();
  const key = projectId ?? "_";
  all[key] = [...(all[key] ?? []).filter((s) => s.id !== seg.id), seg];
  localStorage.setItem(LS_KEY, JSON.stringify(all));
  return all[key];
}

export function deleteSegment(projectId: string | undefined, id: string): SavedSegment[] {
  const all = (() => {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY) ?? "{}") as Record<string, SavedSegment[]>;
    } catch {
      return {};
    }
  })();
  const key = projectId ?? "_";
  all[key] = (all[key] ?? []).filter((s) => s.id !== id);
  localStorage.setItem(LS_KEY, JSON.stringify(all));
  return all[key];
}

interface RemoteSegment {
  id: string;
  name: string;
  filters: SessionFilters;
  createdAt: string;
}

export function listRemoteSegments(projectId: string): Promise<SavedSegment[]> {
  return request<{ items: RemoteSegment[] }>(`/v1/projects/${encodeURIComponent(projectId)}/segments`).then((r) =>
    (r.items ?? []).map((s) => ({ id: s.id, name: s.name, filters: s.filters ?? {}, createdAt: s.createdAt })),
  );
}

export function createRemoteSegment(projectId: string, seg: Pick<SavedSegment, "name" | "filters">): Promise<SavedSegment> {
  return request<RemoteSegment>(`/v1/projects/${encodeURIComponent(projectId)}/segments`, { method: "POST", body: { name: seg.name, filters: seg.filters, shared: true } }).then((s) => ({
    id: s.id,
    name: s.name,
    filters: s.filters ?? {},
    createdAt: s.createdAt,
  }));
}

export function deleteRemoteSegment(projectId: string, id: string): Promise<void> {
  return request<void>(`/v1/projects/${encodeURIComponent(projectId)}/segments/${encodeURIComponent(id)}`, { method: "DELETE" });
}
